// deployment_class: customer_env_allowed
/**
 * Visible-portals computation. Plan 14 §Task 7 + Spec 12 §4.3.
 *
 * Pure function: takes a snapshot of the user's auth state and returns a
 * map of which portals the SPA shell should render switcher entries for.
 *
 * The **Marketplace** and **Data Analytics** switcher entries are reserved for
 * INTERNAL admins — the exact holders of `internalops.read`
 * (`kathedral_dev_admin` · `kathedral_super_admin`). Customer roles do NOT see
 * them (E3 direction 2026-07-02 · `bug_UI_1a`). Note the CEO (`pha_ed`) holds
 * `analytics.read` for their OWN executive dashboard — a naming collision with
 * the internal Data-Analytics *Portal* — so gating on `analytics.read` leaked
 * the portal to the CEO; the correct gate is `internalops.read`.
 * (This supersedes the prior "Marketplace browse is public / always visible"
 * behavior — already void under the 2026-06-20 full-auth-gate lock.)
 *
 * Sales is scope-gated (`sales.read`); Partnership is org-membership-gated
 * (visible if the user belongs to ≥ 1 partnership-scoped organization).
 */

import type { AuthOrgMembership } from '../auth/auth-store';

export type PortalId = 'dashboard' | 'marketplace' | 'partnership' | 'sales' | 'data-analytics';

export interface VisiblePortalsInput {
  /** Caller's effective scopes (from /v3/auth/me). Empty for unauth. */
  scopes: string[];
  /** Caller's organization memberships. */
  organizations: AuthOrgMembership[];
  /**
   * Whether the caller is authenticated. Marketplace browse is public, so
   * we still report marketplace = true even when this is false.
   */
  authenticated: boolean;
}

export interface VisiblePortals {
  /** The Executive/CEO cockpit (also the Staff view, scope-filtered). Shown to
   *  internal admins so they can view the customer dashboards/modules (E3
   *  2026-07-06); CEO-tier roles reach it via their landing, not the switcher. */
  dashboard: boolean;
  marketplace: boolean;
  partnership: boolean;
  sales: boolean;
  dataAnalytics: boolean;
}

export function computeVisiblePortals(input: VisiblePortalsInput): VisiblePortals {
  const { scopes, organizations } = input;
  // Internal admins (`internalops.read` ⇒ kathedral_dev/platform/super_admin) see
  // EVERY surface for insight/debug/QA (E3 2026-07-06). Non-admins keep the
  // narrow, role-appropriate gating.
  const internalAdmin = scopes.includes('internalops.read');
  return {
    dashboard: internalAdmin,
    marketplace: internalAdmin,
    partnership: internalAdmin || organizations.some((org) => isPartnershipRole(org.role)),
    sales: internalAdmin || scopes.includes('sales.read'),
    dataAnalytics: internalAdmin,
  };
}

/**
 * A user has Partnership Portal access when at least one of their org
 * memberships carries a role under the `partnership.*` namespace
 * (e.g., `partnership.member`, `partnership.lead`, `partnership.viewer`).
 *
 * Per Spec 12 §6.4: the role-namespace prefix `partnership.` is the
 * canonical signal that the user is on the partnership operating system
 * for that organization.
 */
function isPartnershipRole(role: string): boolean {
  return role === 'partnership' || role.startsWith('partnership.');
}
